import React, { useEffect, useState } from 'react';
import rtoService from '../../services/rtoService';
import toastService from '../../services/toastService';
import { Card, CardBody, CardHeader } from './../../components/card/card.jsx';

// Simple IPv4 CIDR validator (e.g., 10.0.0.0/8)
function isValidIPv4CIDR(value) {
  if (typeof value !== 'string') return false;
  const m = value.trim().match(/^([0-9]{1,3})\.([0-9]{1,3})\.([0-9]{1,3})\.([0-9]{1,3})\/(\d{1,2})$/);
  if (!m) return false;
  const octets = [m[1], m[2], m[3], m[4]].map(n => Number(n));
  const prefix = Number(m[5]);
  if (prefix < 0 || prefix > 32) return false;
  return octets.every(o => o >= 0 && o <= 255);
}

const ListEditor = ({ label, items, setItems, placeholder, onValidate }) => {
  const [input, setInput] = useState('');

  const addItem = () => {
    const v = input.trim();
    if (!v) return;
    if (items.includes(v)) {
      toastService.info(`${label} already includes: ${v}`);
      return;
    }
    if (typeof onValidate === 'function') {
      const res = onValidate(v);
      if (res !== true) {
        const msg = typeof res === 'string' ? res : `Invalid value for ${label}: ${v}`;
        toastService.error(msg);
        return;
      }
    }
    setItems([...items, v]);
    setInput('');
  };

  const removeItem = (idx) => {
    const next = items.slice();
    next.splice(idx, 1);
    setItems(next);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addItem();
    }
  };

  return (
    <div className="mb-4">
      <label className="form-label fw-semibold">{label}</label>
      <div className="d-flex gap-2 mb-2">
        <input
          type="text"
          className="form-control"
          placeholder={placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <button className="btn btn-primary" onClick={addItem}>Add</button>
      </div>
      {items.length === 0 ? (
        <div className="text-muted small">No entries yet.</div>
      ) : (
        <ul className="list-group">
          {items.map((v, idx) => (
            <li key={`${label}-${idx}`} className="list-group-item d-flex justify-content-between align-items-center">
              <code>{v}</code>
              <button className="btn btn-sm btn-outline-danger" onClick={() => removeItem(idx)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const PolicyAllowLists = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approvedCidrs, setApprovedCidrs] = useState([]);
  const [approvedHosts, setApprovedHosts] = useState([]);
  const [error, setError] = useState('');

  const fetchLists = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await rtoService.getPolicyAllowLists();
      setApprovedCidrs(Array.isArray(data.approved_cidrs) ? data.approved_cidrs : []);
      setApprovedHosts(Array.isArray(data.approved_hosts) ? data.approved_hosts : []);
    } catch (e) {
      setError(e.message || 'Failed to load allow-lists');
      toastService.error(e.message || 'Failed to load allow-lists');
    } finally {
      setLoading(false);
    }
  };

  const parseServerError = (e) => {
    if (!e) return 'Failed to update allow-lists';
    if (e.status === 422) {
      // FastAPI style { detail: str | [{ msg, loc, ... }, ...] }
      const d = e.data?.detail;
      if (typeof d === 'string') return d;
      if (Array.isArray(d)) {
        const msgs = d.map((x) => x.msg || x.message || JSON.stringify(x));
        return msgs.join('; ');
      }
    }
    return e.message || 'Failed to update allow-lists';
  };

  const saveLists = async () => {
    setSaving(true);
    setError('');

    // Client-side validation for CIDRs before sending
    const invalidCidrs = approvedCidrs.filter((c) => !isValidIPv4CIDR(c));
    if (invalidCidrs.length > 0) {
      const msg = `Invalid CIDR entr${invalidCidrs.length > 1 ? 'ies' : 'y'}: ${invalidCidrs.join(', ')}`;
      setSaving(false);
      setError(msg);
      toastService.error(msg);
      return;
    }

    try {
      const data = await rtoService.updatePolicyAllowLists(approvedCidrs, approvedHosts);
      setApprovedCidrs(data.approved_cidrs || []);
      setApprovedHosts(data.approved_hosts || []);
      toastService.success('Allow-lists updated');
    } catch (e) {
      const msg = parseServerError(e);
      setError(msg);
      toastService.error(msg);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchLists();
  }, []);

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="h2 mb-0">Policy Allow-Lists</h1>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={fetchLists} disabled={loading}>
            Refresh
          </button>
          <button className="btn btn-primary" onClick={saveLists} disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">{error}</div>
      )}

      <Card>
        <CardHeader>
          <div className="fw-semibold">Runtime Allow-Lists for RT Orchestrator Policy</div>
          <div className="text-muted small">These lists are enforced for target fields like hostnames, IPs, and URIs.</div>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="d-flex align-items-center">
              <div className="spinner-border me-2" role="status" /> Loading…
            </div>
          ) : (
            <>
              <ListEditor
                label="Approved CIDRs"
                items={approvedCidrs}
                setItems={setApprovedCidrs}
                placeholder="e.g. 10.0.0.0/8"
                onValidate={(v) => (isValidIPv4CIDR(v) ? true : 'Please enter a valid IPv4 CIDR, e.g. 10.0.0.0/24')}
              />

              <ListEditor
                label="Approved Hosts"
                items={approvedHosts}
                setItems={setApprovedHosts}
                placeholder="e.g. dc01.example.local or 10.0.0.10"
              />

              <div className="text-muted small mt-3">
                Notes:
                <ul className="mt-2">
                  <li>Changes take effect immediately for new policy evaluations.</li>
                  <li>Invalid CIDR entries will be rejected by the server.</li>
                </ul>
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default PolicyAllowLists;